import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import * as blueprint from '@adc/blueprint'
import { Text } from '@adc/ui-adc'
import * as graph from '@adc/sdk'
import {
  formatMoneyWithNumeral,
  formatMoneyWithCentsExceptZero,
} from 'common/utils/formatters'
import {
  ReserveTooltip,
  MatchBidTooltip,
  BidDepositTooltip,
} from 'common/components/bidBanner/tooltips'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const LabelValue = ({ label, value, valueColor, ...rest }) => {
  return (
    <div className={cx('bid-detail')} {...rest}>
      {typeof label === 'string' ? (
        <Text
          className={cx('u-mb-1')}
          content={label}
          data-elm-id='label'
          color='gray'
        />
      ) : (
        label
      )}
      <Text
        content={value}
        data-elm-id='value'
        weight='heavy'
        color={valueColor || 'gray'}
      />
    </div>
  )
}

LabelValue.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.string,
  valueColor: Text.propTypes.color,
}

const OpeningBid = ({ openingBid }) => {
  const value = formatMoneyWithCentsExceptZero(openingBid, '-')
  return (
    <LabelValue label='Opening Bid' value={value} data-elm-id='opening_bid' />
  )
}

OpeningBid.propTypes = {
  openingBid: PropTypes.number,
}

const CurrentBid = ({ currentBidAmount }) => {
  const value = formatMoneyWithCentsExceptZero(currentBidAmount, '-')
  return (
    <LabelValue label='Current Bid' value={value} data-elm-id='current_bid' />
  )
}

CurrentBid.propTypes = {
  currentBidAmount: PropTypes.number,
}

const OpeningOrCurrentBid = ({ segment, listing }) => {
  if (segment?.bid_count) {
    return (
      <CurrentBid currentBidAmount={segment.current_highest_bid?.bid_amount} />
    )
  } else if (!listing.listing_summary.show_opening_bid) {
    return <OpeningBid openingBid={null} />
  } else {
    return <OpeningBid openingBid={segment?.starting_bid_amount} />
  }
}

OpeningOrCurrentBid.propTypes = {
  segment: PropTypes.object,
  listing: PropTypes.object,
}

const BidIncrement = ({ bidIncrement }) => {
  const value = formatMoneyWithNumeral(bidIncrement, '-')
  return (
    <LabelValue
      label='Bid Increment'
      value={value}
      data-elm-id='bid_increment'
    />
  )
}

BidIncrement.propTypes = {
  bidIncrement: PropTypes.number,
}

const Reserve = ({ segment }) => {
  let reserveLabelValue = ''
  switch (segment.reserve_status) {
    case graph.enums.ReserveStatus.ReserveMet:
      reserveLabelValue = 'Met'
      break
    case graph.enums.ReserveStatus.ReserveNotMet:
    case graph.enums.ReserveStatus.SubjectToReserveMet:
      reserveLabelValue = 'Not Met'
      break
    case graph.enums.ReserveStatus.ReserveNotSet:
      reserveLabelValue = 'Not Set'
      break
  }

  const hasReserveBeenMet =
    segment.reserve_status === graph.enums.ReserveStatus.ReserveMet
  return (
    <LabelValue
      label={
        <div className={cx('u-mb-1')}>
          <Text
            as='span'
            className={cx('u-mr-1')}
            content='Reserve'
            data-elm-id='label'
            color='gray'
          />
          <ReserveTooltip />
        </div>
      }
      data-elm-id='reserve'
      value={reserveLabelValue}
      valueColor={hasReserveBeenMet ? 'success' : 'caution'}
    />
  )
}

Reserve.propTypes = {
  segment: PropTypes.object,
}

const EstCreditBid = ({ segment }) => {
  let props = {}
  switch (segment.reserve_status) {
    case graph.enums.ReserveStatus.ReserveMet:
      props = {
        value: 'Exceeded',
        valueColor: 'success',
      }
      break
    case graph.enums.ReserveStatus.ReserveNotMet:
      props = {
        value: 'Not Exceeded',
        valueColor: 'caution',
      }
      break
    case graph.enums.ReserveStatus.ReserveNotSet:
      props = {
        value: 'Not Set',
        valueColor: 'caution',
      }
      break
  }
  return (
    <LabelValue
      label='Est. Credit Bid'
      data-elm-id='est_credit_bid'
      {...props}
    />
  )
}

EstCreditBid.propTypes = {
  segment: PropTypes.object.isRequired,
}

const BidDeposit = ({ bidDeposit }) => {
  if (!bidDeposit) return null
  const value = blueprint.utils.formatMoney(bidDeposit, {}, true)
  return (
    <LabelValue
      label={
        <div className={cx('u-mb-1')}>
          <Text
            as='span'
            className={cx('u-mr-1')}
            content='Bid Deposit'
            data-elm-id='label'
            color='gray'
          />
          <BidDepositTooltip />
        </div>
      }
      value={value}
      data-elm-id='bid_deposit'
      valueColor='success'
    />
  )
}

BidDeposit.propTypes = {
  bidDeposit: PropTypes.number,
}

export const Before = ({
  containerClassName,
  segment,
  listing,
  bidDeposit,
}) => {
  return (
    <div className={cx('container', containerClassName)}>
      <OpeningOrCurrentBid segment={segment} listing={listing} />
      <LabelValue
        label='Bid Increment'
        value='N/A'
        data-elm-id='bid_increment'
      />
      <BidDeposit bidDeposit={bidDeposit} />
    </div>
  )
}

export const During = ({
  containerClassName,
  listing,
  segment,
  bidDeposit,
}) => {
  return (
    <div className={cx('container', containerClassName)}>
      <OpeningOrCurrentBid segment={segment} listing={listing} />
      <BidIncrement bidIncrement={segment.current_increment_amount} />
      {!listing.__methods__?.isTrustee() ? (
        <Reserve segment={segment} />
      ) : (
        <EstCreditBid segment={segment} />
      )}
      <BidDeposit bidDeposit={bidDeposit} />
    </div>
  )
}

export const After = ({ containerClassName, listing, segment, bidDeposit }) => {
  return (
    <div className={cx('container', containerClassName)}>
      {!listing.__methods__?.isTrustee() ? (
        <Reserve segment={segment} />
      ) : (
        <EstCreditBid listing={listing} segment={segment} />
      )}
      <BidDeposit bidDeposit={bidDeposit} />
    </div>
  )
}

const MatchBidReserve = ({ hasReserveBeenMet }) => {
  return (
    <LabelValue
      label={
        <div className={cx('u-mb-1')}>
          <Text
            as='span'
            className={cx('u-mr-1')}
            content='Reserve'
            data-elm-id='label'
            color='gray'
          />
          <ReserveTooltip />
        </div>
      }
      data-elm-id='reserve'
      value={hasReserveBeenMet ? 'Met' : 'Not Met'}
      valueColor={hasReserveBeenMet ? 'success' : 'caution'}
    />
  )
}

MatchBidReserve.propTypes = {
  hasReserveBeenMet: PropTypes.bool,
}

export const MatchBidAfter = ({ matchBidCount, hasReserveBeenMet }) => {
  return (
    <div className={cx('container')}>
      <MatchBidReserve hasReserveBeenMet={hasReserveBeenMet} />
      {matchBidCount > 0 && (
        <LabelValue
          label={
            <div className={cx('u-mb-1')}>
              <Text
                as='span'
                className={cx('u-mr-1')}
                content='Backup Match Bidders'
                data-elm-id='label'
                color='gray'
              />
              <MatchBidTooltip />
            </div>
          }
          data-elm-id='bid_details_after_match_the_bid'
          value={matchBidCount}
        />
      )}
    </div>
  )
}

MatchBidAfter.propTypes = {
  hasReserveBeenMet: PropTypes.bool,
  matchBidCount: PropTypes.number,
}

Before.propTypes =
  During.propTypes =
  After.propTypes =
    {
      containerClassName: PropTypes.string,
      listing: PropTypes.object,
      segment: PropTypes.object,
      bidDeposit: PropTypes.number,
    }
